import React, { useState, useEffect, useMemo } from 'react';
import { 
  Input, 
  Card, 
  Row, 
  Col, 
  Button, 
  Typography, 
  Spin, 
  Empty, 
  Badge,
  Image,
  List,
  Divider,
  message
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Plus, X, ShoppingCart, Package, ChevronUp, ChevronDown } from 'lucide-react';
import styled from '@emotion/styled';
import useProductOpenApiClient from 'apiClients/useProductOpenApiClient';
import useProductGroupOpenApi from 'apiClients/useProductGroupOpenApiClient';
import { components } from 'apiClients/base/schema';

const { Title, Text } = Typography;
const { Search } = Input;

type ProductResponse = components["schemas"]["ProductResponse"];

export interface SelectedViewProduct {
  id: number;
  name: string;
  sortOrder: number;
  mainItemNo: string;
  imageUrl?: string;
}

interface ProductSelectViewProps {
  selectedProducts: SelectedViewProduct[];
  onProductsChange: (products: SelectedViewProduct[]) => void;
  title?: string;
  maxHeight?: number;
  productGroupId?: number; // Add this for mutations
  onProductUnassigned?: (productId: number) => void; // Callback when product is unassigned
}

const Container = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 1rem;
`;

const SearchSection = styled.div`
  margin-bottom: 1rem;
`;

const ProductGrid = styled.div<{ maxHeight: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  max-height: ${props => props.maxHeight}px;
  overflow-y: auto;
  padding: 0.5rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ProductCard = styled(Card)<{ isSelected: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.isSelected ? '#1890ff' : 'transparent'};
  background: ${props => props.isSelected ? '#e6f7ff' : 'white'};
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  .ant-card-body {
    padding: 12px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ProductTitle = styled(Text)`
  font-weight: 600;
  color: #262626;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProductItemNo = styled(Text)`
  color: #8c8c8c;
  font-size: 0.875rem;
`;

const SelectedSection = styled.div`
  margin-top: 1.5rem;
`;

const SelectedList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  margin-bottom: 1rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
`;

const SelectedItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SelectedItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const SelectedItemText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductSelectView: React.FC<ProductSelectViewProps> = ({
  selectedProducts = [],
  onProductsChange,
  title = "Välj produkter",
  maxHeight = 400,
  productGroupId,
  onProductUnassigned
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [unassigning, setUnassigning] = useState<number | null>(null);
  const { searchProduct } = useProductOpenApiClient();
  const { unassignProductsByIds } = useProductGroupOpenApi();

  // Search products with debounce
  useEffect(() => {
    if (searchTerm && searchTerm.length >= 2) {
      setLoading(true);
      
      const timer = setTimeout(async () => {
        try {
          const results = await searchProduct(searchTerm);
          setSearchResults(results || []);
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } finally {
          setLoading(false);
        }
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setLoading(false);
    }
  }, [searchTerm, searchProduct]);

  // Check if product is selected
  const isProductSelected = (productId: number) => {
    return selectedProducts.some(p => p.id === productId);
  };

  // Toggle product selection
  const toggleProduct = (product: ProductResponse) => {
    if (!product.id) return;

    const isSelected = isProductSelected(product.id);
    
    if (isSelected) {
      // Remove from selection
      const newSelection = selectedProducts.filter(p => p.id !== product.id);
      onProductsChange(newSelection);
    } else {
      // Add to selection
      const newProduct: SelectedViewProduct = {
        id: product.id,
        sortOrder: selectedProducts.length,
        name: product.name || 'Namn saknas',
        mainItemNo: product.mainItemNo || '',
        imageUrl: product.images?.[0]?.previewUrl || undefined
      };
      onProductsChange([...selectedProducts, newProduct]);
    }
  };

  // Remove product from selection
  const removeProduct = async (productId: number) => {
    // If productGroupId is provided, call the API to unassign the product
    if (productGroupId) {
      setUnassigning(productId);
      try {
        await unassignProductsByIds(productGroupId, [productId]);
        message.success('Produkten har tagits bort från produktgruppen');
        
        // Call the callback if provided
        if (onProductUnassigned) {
          onProductUnassigned(productId);
        }
      } catch (error) {
        message.error('Fel vid borttagning av produkt från produktgrupp');
        console.error('Error unassigning product:', error);
        setUnassigning(null);
        return; // Don't update local state if API call failed
      } finally {
        setUnassigning(null);
      }
    }

    // Update local state
    const newSelection = selectedProducts.filter(p => p.id !== productId);
    // Update sort orders
    const reorderedSelection = newSelection.map((product, index) => ({
      ...product,
      sortOrder: index
    }));
    onProductsChange(reorderedSelection);
  };

  // Move product up or down in the list
  const moveProduct = (productId: number, direction: 'up' | 'down') => {
    const currentIndex = selectedProducts.findIndex(p => p.id === productId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= selectedProducts.length) return;

    const newSelection = [...selectedProducts];
    
    // Swap the products
    [newSelection[currentIndex], newSelection[newIndex]] = [newSelection[newIndex], newSelection[currentIndex]];
    
    // Update sort orders
    const reorderedSelection = newSelection.map((product, index) => ({
      ...product,
      sortOrder: index
    }));
    
    onProductsChange(reorderedSelection);
  };

  // Get image URL
  const getImageUrl = (product: ProductResponse): string => {
    return product.images?.[0]?.previewUrl || product.images?.[0]?.url || '';
  };

  return (
    <Container>
      <Title level={5} style={{ margin: '0 0 1rem 0' }}>
        {title}
        {selectedProducts.length > 0 && (
          <Badge 
            count={selectedProducts.length} 
            style={{ marginLeft: '0.5rem' }}
            showZero={false}
          />
        )}
      </Title>

      <SearchSection>
        <Search
          placeholder="Sök produkter (minst 2 tecken)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          size="large"
          prefix={<SearchOutlined />}
        />
      </SearchSection>

      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && searchTerm && searchTerm.length >= 2 && (
        <ProductGrid maxHeight={maxHeight}>
          {searchResults.length > 0 ? (
            searchResults.map((product) => (
              <ProductCard
                key={product.id}
                isSelected={isProductSelected(product.id!)}
                onClick={() => toggleProduct(product)}
                size="small"
                hoverable
              >
                <Row gutter={[8, 8]} align="middle">
                  <Col span={6}>
                    <Image
                      src={getImageUrl(product)}
                      alt={product.name || 'Produkt'}
                      width={50}
                      height={50}
                      style={{ objectFit: 'cover', borderRadius: '4px' }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ij1xUF8A8REMdxtuKcc4AcY4ht1uMN2IZ3wA3YCrADO4ArcAWKMgRoA5bg"
                      preview={false}
                    />
                  </Col>
                  <Col span={18}>
                    <ProductInfo>
                      <ProductTitle>{product.name || 'Namn saknas'}</ProductTitle>
                      <ProductItemNo>Art.nr: {product.mainItemNo || 'Saknas'}</ProductItemNo>
                      {isProductSelected(product.id!) && (
                        <Badge status="success" text="Vald" />
                      )}
                    </ProductInfo>
                  </Col>
                </Row>
              </ProductCard>
            ))
          ) : (
            <div style={{ gridColumn: '1 / -1' }}>
              <Empty 
                description="Inga produkter hittades"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </ProductGrid>
      )}

      {selectedProducts.length > 0 && (
        <SelectedSection>
          <Divider>
            <Package size={16} style={{ marginRight: '0.5rem', marginBottom: '-3px' }} />
            Valda produkter ({selectedProducts.length})
          </Divider>
          
          <SelectedList>
            {selectedProducts.map((product, index) => (
              <SelectedItem key={product.id}>
                <SelectedItemInfo>
                  {product.imageUrl && (
                    <Image
                      src={product.imageUrl}
                      alt={product.name}
                      width={40}
                      height={40}
                      style={{ objectFit: 'cover', borderRadius: '4px' }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ij1xUF8A8REMdxtuKcc4AcY4ht1uMN2IZ3wA3YCrADO4ArcAWKMgRoA5bg"
                      preview={false}
                    />
                  )}
                  <SelectedItemText>
                    <Text strong>{product.name}</Text>
                    <Text type="secondary" style={{ fontSize: '0.875rem' }}>
                      Art.nr: {product.mainItemNo}
                    </Text>
                  </SelectedItemText>
                </SelectedItemInfo>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Button
                    type="text"
                    size="small"
                    icon={<ChevronUp size={14} />}
                    onClick={() => moveProduct(product.id, 'up')}
                    disabled={index === 0}
                    title="Flytta upp"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ChevronDown size={14} />}
                    onClick={() => moveProduct(product.id, 'down')}
                    disabled={index === selectedProducts.length - 1}
                    title="Flytta ner"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<X size={16} />}
                    onClick={() => removeProduct(product.id)}
                    loading={unassigning === product.id}
                    danger
                    title="Ta bort"
                  />
                </div>
              </SelectedItem>
            ))}
          </SelectedList>
        </SelectedSection>
      )}
    </Container>
  );
};

export default ProductSelectView;
